import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  nome: "",
  email: "",
  telefono: "",
  tipologia: "non-so",
  messaggio: "",
  website: "", // honeypot
};

export const ContactForm = () => {
  const [data, setData] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (data.nome.trim().length < 2) return toast.error("Inserisci il tuo nome");
    if (!/^\S+@\S+\.\S+$/.test(data.email)) return toast.error("Email non valida");
    if (data.messaggio.trim().length < 10) return toast.error("Scrivi un messaggio (min. 10 caratteri)");

    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, data, { timeout: 20000 });
      setSuccess(true);
      setData(initial);
      toast.success("Messaggio inviato! Francesca ti risponderà al più presto.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Invio fallito. Riprova o scrivici via email.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div
        data-testid="contact-success"
        className="flex flex-col items-start gap-4 bg-white border border-brand-line p-8 rounded-sm"
      >
        <div className="h-12 w-12 flex items-center justify-center bg-brand-ink text-white rounded-sm">
          <CheckCircle2 size={22} strokeWidth={1.8} />
        </div>
        <h3 className="font-display text-2xl text-brand-ink leading-tight">
          Messaggio inviato.
        </h3>
        <p className="text-brand-muted leading-relaxed">
          Grazie per avermi scritto. Ti risponderò personalmente via email nelle prossime ore lavorative.
        </p>
        <button
          onClick={() => setSuccess(false)}
          data-testid="contact-send-another"
          className="mt-2 text-sm font-medium text-brand-ink border-b border-brand-ink/30 hover:border-brand-ink transition-colors"
        >
          Invia un altro messaggio
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="contact-form"
      className="bg-white border border-brand-line p-7 sm:p-9 rounded-sm space-y-5"
      noValidate
    >
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-brand-muted font-semibold">
          Preferisci scrivere?
        </div>
        <h3 className="mt-3 font-display text-2xl sm:text-3xl text-brand-ink leading-tight">
          Parliamo del tuo immobile.
        </h3>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Nome e cognome *" name="nome" testid="contact-field-nome">
          <input
            type="text"
            value={data.nome}
            onChange={update("nome")}
            required
            data-testid="contact-input-nome"
            className="input-base"
            placeholder="Mario Rossi"
          />
        </Field>
        <Field label="Email *" name="email" testid="contact-field-email">
          <input
            type="email"
            value={data.email}
            onChange={update("email")}
            required
            data-testid="contact-input-email"
            className="input-base"
            placeholder="tu@email.com"
          />
        </Field>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Telefono (facoltativo)" name="telefono" testid="contact-field-telefono">
          <input
            type="tel"
            value={data.telefono}
            onChange={update("telefono")}
            data-testid="contact-input-telefono"
            className="input-base"
            placeholder="+39 ..."
          />
        </Field>
        <Field label="Ti interessa" name="tipologia" testid="contact-field-tipologia">
          <select
            value={data.tipologia}
            onChange={update("tipologia")}
            data-testid="contact-input-tipologia"
            className="input-base cursor-pointer"
          >
            <option value="non-so">Da valutare insieme</option>
            <option value="breve">Affitto breve</option>
            <option value="transitorio">Affitto transitorio</option>
            <option value="4+4">Affitto 4+4</option>
          </select>
        </Field>
      </div>

      <Field label="Messaggio *" name="messaggio" testid="contact-field-messaggio">
        <textarea
          value={data.messaggio}
          onChange={update("messaggio")}
          required
          minLength={10}
          rows={4}
          data-testid="contact-input-messaggio"
          className="input-base resize-none"
          placeholder="Raccontami del tuo immobile: zona, metratura, situazione attuale..."
        />
      </Field>

      {/* Honeypot: hidden from users */}
      <div className="hidden" aria-hidden="true">
        <label>Non compilare
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={data.website}
            onChange={update("website")}
          />
        </label>
      </div>

      <button
        type="submit"
        disabled={submitting}
        data-testid="contact-submit"
        className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-brand-ink text-white px-7 py-3.5 rounded-sm font-medium hover:bg-brand-inkHover transition-all disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {submitting ? (
          <>
            <Loader2 size={18} className="animate-spin" />
            Invio in corso...
          </>
        ) : (
          <>
            Invia messaggio
            <Send size={16} className="transition-transform group-hover:translate-x-0.5" />
          </>
        )}
      </button>

      <p className="text-xs text-brand-muted leading-relaxed">
        Inviando accetti di essere ricontattata da Francesca Gorla all'indirizzo indicato.
      </p>
    </form>
  );
};

const Field = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="block text-xs uppercase tracking-[0.18em] font-semibold text-brand-muted mb-2">
      {label}
    </span>
    {children}
  </label>
);
